import React, { useCallback, useState } from 'react';
import Icon from '@mui/material/Icon';
import Button from '@mui/material/Button';
import { Card, CardContent, CardMedia, Tooltip, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import { useDropzone } from 'react-dropzone';
import _ from '@lodash';
import { Controller, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon/FuseSvgIcon';
import { selectUser } from 'app/store/userSlice';
import IconButton from '@mui/material/IconButton';
import { uploadFile } from '../store/filesSlice';
import { uploadedAttachmentOf } from 'app/main/apps/common/fileSource';
import { canViewFile, checkNullOrUndefined, getFileImageSrc, handleDownload, isValidObject, onFileViwerClick } from '../../common/AuraFunctions';
import MobileFileUploadInput from './MobileFileUploadInput';

function Attachments(props) {
  const dispatch = useDispatch();
  const methods = useFormContext();
  const { control, watch, setValue } = methods;
  const attachmentType = props.type || 'attachment';
  const attachmentCategory = props.category || 'document';
  const attachmentsType = props.attachmentsType || `${attachmentType}s`;
  const acceptedFileTypes = props.acceptedFileTypes || {
    'image/jpeg': ['.jpeg', '.jpg', '.png'],
    'application/pdf': ['.pdf'],
    'application/postscript': ['.ai', '.plt', '.eps'],
    'application/vnd.dxf': ['.dxf'],
    'image/heic': ['.heic', '.heif']
  }
  const acceptedFileExtensions = props.acceptedFileExtensions || "Images, PDF, AI, PLT, EPS or DXF";
  // Flatten all extensions and join them into a string
  const mobileAcceptString = acceptedFileTypes && isValidObject(acceptedFileTypes) ? _.chain(acceptedFileTypes)
    .values()               // Get arrays of extensions
    .flatten()              // Flatten into a single array
    .uniq()                 // Optional: remove duplicates
    .join(',')              // Join into a comma-separated string
    .value() : ".jpeg,.jpg,.png,.pdf,.ai,.plt,.eps,.dxf,.heic,.heif";


  const newAttachmentsType = props.newAttachmentsType || `new_${attachmentsType}`;
  const attachments = watch(attachmentsType);
  const newAttachments = watch(newAttachmentsType);
  const user = useSelector(selectUser)
  const [Loading, setLoading] = useState(false);
  const width = props.width || "w-[16rem]";
  const height = props.height || "h-[13rem]";
  const fontSize = props.fontSize || "text-14";
  const canDeleteExisting = !checkNullOrUndefined(props.canDeleteExisting) ? props.canDeleteExisting : true;

  const onDrop = useCallback(async (acceptedFiles, value) => {
    let existingAttachemnts = watch(newAttachmentsType);

    async function readAttachmentAsync(file) {
      return new Promise((resolve, reject) => {
        if (!file) {
          return;
        }
        dispatch(
          uploadFile({ file, addTo: props.addTo, ...(props.payload || {}) })
        ).then((response) => {
          const saved = response?.payload;
          // PGSS-5483: preview what was STORED, not what was picked. An iPhone photograph is
          // converted to JPEG on upload (PGSS-5479), so the local file and the stored file are no
          // longer the same thing -- and the browser cannot render the local one, which is why the
          // tile fell back to a download icon until the page was reloaded. The response already
          // carries the converted content and its type; nothing extra needs fetching.
          // PGSS-5505: the upload response now usually carries a signed link instead of the bytes,
          // so a guard on `data` alone dropped through to the local-file fallback below -- which is
          // the PGSS-5483 bug again, and worst for an HEIC, whose local copy the browser cannot
          // render at all. Accept either source and let resolveFileSrc pick.
          // PGSS-5577: uploadedAttachmentOf also keeps download_url, so Download on a file just
          // uploaded saves it rather than opening it in a new tab.
          const uploaded = uploadedAttachmentOf(saved, attachmentCategory);
          if (uploaded) {
            resolve(uploaded);
            return;
          }
          // Nothing usable came back -- fall back to the local file so the tile still names the
          // upload rather than vanishing.
          const reader = new FileReader();
          reader.onload = () => {
            resolve({
              id: saved?.id,
              url: `data:${file.type};base64,${btoa(reader?.result)}`,
              type: attachmentCategory,
              image_uuid: saved?.etchingUUID,
              name: saved?.filename || file.name,
            });
          };
          reader.onerror = reject;
          reader.readAsBinaryString(file);
        });
      });
    }
    setLoading(true);
    // Process each file asynchronously and collect all new attachments
    const newAttachmentsTemp = await Promise.all(
      acceptedFiles.map((file) => readAttachmentAsync(file)) // Process all files
    );
    if(checkNullOrUndefined(existingAttachemnts)) {
      existingAttachemnts = [];
    }
    setValue(
      newAttachmentsType,
      [...newAttachmentsTemp, ...existingAttachemnts],
      { shouldDirty: true }
    );
    if(props.onAttachmentChange) {
      props.onAttachmentChange()
    }
    setLoading(false);
    if (props.recentFiles) {
      props.recentFiles(...newAttachmentsTemp);
    }
  }, []);

  function isMobileDevice() {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    multiple: !checkNullOrUndefined(props.acceptMultipleFiles) ? props.acceptMultipleFiles : true,
    noKeyboard: true
  });

  function clearAttachment(event, value) {
    const filteredAttachments = attachments.filter((item) => item.id !== value.id);
    setValue(attachmentsType, filteredAttachments, { shouldDirty: true });
    if(props.onAttachmentChange) {
      props.onAttachmentChange()
    }
  }

  function clearNewAttachment(event, value) {
    const filteredNewAttachments = newAttachments.filter((item) => item.id !== value.id);
    setValue(newAttachmentsType, filteredNewAttachments, { shouldDirty: true });
    if(props.onAttachmentChange) {
      props.onAttachmentChange()
    }
  }

  return (
    <div className={`w-full ${props?.style?.rows && "md:flex md:flex-row"}`}>
      <div
        className={`flex justify-center sm:justify-start items-start flex-wrap ${
          props?.style?.rows ? "md:w-1/3" : "-mx-16 mw-10/12 !m-auto"
        }`}
      >
        {props.allowAdd && (
         <Controller
            name={newAttachmentsType}
            defaultValue=""
            control={control}
            render={({ field: { value } }) => (
              isMobileDevice() ? (
                <MobileFileUploadInput
                  loading={Loading}
                  accept={mobileAcceptString}
                  // accept=".jpeg,.jpg,.png,.pdf,.ai,.plt,.eps,.dxf,.heic,.heif"
                  onFilesSelected={async (files) => {
                    setLoading(true);
                    await onDrop(files);
                    setLoading(false);
                  }}
                  message="Tap to Select Files"
                  subMessage={
                    props?.display?.sub_message ||
                    `Upload your documents ${acceptedFileExtensions} here.`
                  }
                />
              ) : (
                <div
                  {...getRootProps()}
                  className="relative flex flex-col items-center justify-center p-24 mt-14 border-2 border-dashed !border-blue-600/50 outline-none transition hover:!border-blue-600 focus:!border-blue-600 cursor-pointer w-full max-w-md mx-auto min-h-[150px]"
                  style={Loading ? { pointerEvents: "none" } : {}}
                >
                  <input {...getInputProps()} disabled={Loading} />

                  {Loading ? (
                    <div className="absolute inset-0 bg-white bg-opacity-100 z-10 flex items-center justify-center">
                      <CircularProgress color="inherit" />
                    </div>
                  ) : (
                    <div className="w-full flex flex-col items-center">
                      <Icon fontSize="large" color="action" sx={{ fontSize: "6rem" }}>
                        cloud_upload
                      </Icon>
                      <p>{props?.display?.message || "Drag & Drop Files Here"}</p>
                      <p className="text-gray-500 text-center">
                        {props?.display?.sub_message ||
                          `Upload your documents ${acceptedFileExtensions} here.`}
                      </p>
                      <Button
                        type="button"
                        variant="contained"
                        color="secondary"
                        className={`mt-14 rounded-md ${
                          props.smallButton ? "h-[32px] min-h-[32px] max-h-[32px]" : ""
                        }`}
                      >
                        {props?.display?.button_name || "Choose files to upload"}
                      </Button>
                    </div>
                  )}
                </div>
              )
            )}
          />
        )}
      </div>
      <div
        className={`flex justify-center flex-wrap ${
          props?.style?.rows ? "w-2/3 !m-auto gap-14 py-12" : "-mx-16 mt-14 gap-14"
        }`}
      >
        <Controller
          name="attachmentId"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) =>
            attachments ? (
              attachments.map((media, index) => {
                return (
                  <div key={media.id}>
                    <Card className={`${width} rounded-md `}>
                      <div className="relative">
                        <div>
                          <CardMedia
                            key={media.id}
                            className={`${width} ${height} cursor-pointer bg-contain ${
                              media.id === value && "featured"
                            }`}
                            image={getFileImageSrc(media)}
                            tabIndex={0}
                            title="Click here to view or download document"
                            onClick={() => {
                              onFileViwerClick(media);
                            }}
                            onKeyDown={() => {}}
                          />
                        </div>
                        {canDeleteExisting &&
                          <IconButton
                            className="attachmentStar top-1.5 right-1.5 w-28 h-28 absolute z-10"
                            onClick={(ev) => {
                              clearAttachment(ev, media);
                            }}
                            size="small"
                            sx={{ background: "white!important" }}
                          >
                            <Icon>clear</Icon>
                          </IconButton>
                        }
                      </div>
                      <CardContent className="!p-0">
                        <Tooltip title={media?.filename || media?.name}>
                          <Typography className="truncate text-center text-14 px-10 py-4">
                            {media?.filename || media?.name}
                          </Typography>
                        </Tooltip>
                        <div className="flex flex-row items-center justify-center border-t space-x-12 px-10 py-6 w-full bg-[#f3f3f3]">
                          {canViewFile(media) && (
                            <Typography
                              variant="body2"
                              color="secondary"
                              className="cursor-pointer font-medium hover:font-bold"
                              onClick={() => onFileViwerClick(media)}
                            >
                              View
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="secondary"
                            className="cursor-pointer font-medium hover:font-bold"
                            onClick={() => handleDownload(media)}
                          >
                            Download
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })
            ) : (
              <></>
            )
          }
        />
        <Controller
          name="attachmentNewId"
          control={control}
          defaultValue=""
          render={({ field: { onChange, value } }) =>
            newAttachments && !props.hideFileList ? (
              newAttachments.map((media) => {
                return (
                  <div key={media.id}>
                    <Card className={`${width} rounded-md `}>
                      <div className="relative">
                        <div>
                          <CardMedia
                            key={media.id}
                            className={`${width} ${height} cursor-pointer bg-contain ${
                              props.selected === media.id && "border-blue-500"
                            }`}
                            image={getFileImageSrc(media)}
                            title="Click here to view or download document"
                            onClick={() => {
                              onFileViwerClick(media);
                              onChange(media.id);
                              if (props.setSelected) props.setSelected(media);
                            }}
                            onKeyDown={() => onChange(media.id)}
                          />
                          {props.selected === media.id && (
                            <div className="absolute top-2 left-2 z-10 w-14 h-14 rounded-full">
                              <FuseSvgIcon size={28} color="success">
                                heroicons-solid:check-circle
                              </FuseSvgIcon>
                            </div>
                          )}
                        </div>
                        <IconButton
                          className="attachmentStar top-1.5 right-1.5 w-28 h-28 absolute z-10"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            clearNewAttachment(ev, media);
                          }}
                          size="small"
                          sx={{ background: "white!important" }}
                        >
                          <Icon>clear</Icon>
                        </IconButton>
                      </div>
                      <CardContent className="!p-0">
                        <Tooltip title={media?.filename || media?.name}>
                          <Typography className={`truncate text-center px-10 py-4 ${fontSize}`}>
                            {media?.filename || media?.name}
                          </Typography>
                        </Tooltip>
                        <div className="flex flex-row items-center justify-center border-t space-x-12 px-10 py-6 w-full bg-[#f3f3f3]">
                          {canViewFile(media) && (
                            <Typography
                              variant="body2"
                              color="secondary"
                              className={`cursor-pointer font-medium hover:font-bold ${fontSize}`}
                              onClick={() => onFileViwerClick(media)}
                            >
                              View
                            </Typography>
                          )}
                          <Typography
                            variant="body2"
                            color="secondary"
                            className={`cursor-pointer font-medium hover:font-bold ${fontSize}`}
                            onClick={() => handleDownload(media)}
                          >
                            Download
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                );
              })
            ) : (
              <></>
            )
          }
        />
      </div>
    </div>
  );
}

export default Attachments;
